import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Game } from '@par/shared/types/game';
import { BottomChatWindow } from './BottomChatWindow';
import { Socket } from 'socket.io-client';
import { OpenGames } from './OpenGames';
import { WeekendOutlined } from '@mui/icons-material';
import { Mode } from '@par/shared/common/types';

export type Chat = {
  color?: string;
  username: string;
  text: string;
  avatarPublicId: string;
  leftChat: boolean;
};

type PropTypes = {
  chat: Chat[];
  focusInput: number;
  gameId: string;
  handleBackToTournamentLobbyClick?: () => void;
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  hideChat: boolean;
  isDrawerOpen: boolean;
  lobbyOpenGames: Game[];
  openedBottomDrawerByClick: boolean;
  profileId: string;
  socket: Socket;
  spectate?: boolean;
  tournament?: number;
  username: string;
  usersInRoom: string[];
};

export const BottomDrawer = ({
  chat,
  focusInput,
  gameId,
  handleBackToTournamentLobbyClick,
  handleJoinGameClick,
  handleLeaveGameClick,
  hideChat,
  isDrawerOpen,
  lobbyOpenGames,
  openedBottomDrawerByClick,
  profileId,
  socket,
  spectate,
  tournament,
  username,
  usersInRoom,
}: PropTypes) => {
  const theme = useTheme();
  const [usersInRoomInit, setUsersInRoomInit] = useState(false);
  const [chatMembers, setChatMembers] = useState<string[]>();

  useEffect(() => {
    if (!usersInRoom || !usersInRoom.length) return;
    if (usersInRoomInit) return;
    setUsersInRoomInit(true);
    setChatMembers(usersInRoom);
  }, [usersInRoom]);

  useEffect(() => {
    if (!usersInRoomInit) return;
    if (!chat || !chat.length) return;
    const membersThatLeft = chat
      .filter((c) => c.leftChat)
      .map((c) => c.username);
    const members = usersInRoom.filter((u) => !membersThatLeft.includes(u));
    setChatMembers(members);
  }, [chat]);

  return (
    <Stack
      direction="row"
      sx={{
        height: '300px',
        backgroundColor: '#191b1d',
        borderTop: '1px solid',
        borderColor: theme.palette.primary.main,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        width={'50%'}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {!hideChat ? (
          <BottomChatWindow
            chat={chat}
            chatMembers={chatMembers}
            focusInput={focusInput}
            gameId={gameId}
            isDrawerOpen={isDrawerOpen}
            openedBottomDrawerByClick={openedBottomDrawerByClick}
            socket={socket}
            spectate={spectate}
            username={username}
          />
        ) : (
          <Typography>
            Chat not applicable in Searched and Ghost matches
          </Typography>
        )}
      </Box>
      <Divider orientation="vertical" flexItem />
      <Stack
        width={'50%'}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'auto',
        }}
      >
        {!tournament ? (
          <>
            <Typography
              variant="subtitle1"
              textAlign={'center'}
              letterSpacing={5}
            >
              QUICK JOIN
            </Typography>
            <OpenGames
              handleJoinGameClick={handleJoinGameClick}
              handleLeaveGameClick={handleLeaveGameClick}
              openGames={lobbyOpenGames}
              profileId={profileId}
            />
          </>
        ) : (
          <Stack spacing={2} display={'flex'} justifyContent={'center'}>
            <Typography fontWeight={600} textAlign={'center'} color="info.main">
              If you made the cut, go back to the Tournament Lobby to join the
              next game.
            </Typography>
            <Box display={'flex'} justifyContent={'center'}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<WeekendOutlined />}
                onClick={handleBackToTournamentLobbyClick}
              >
                Back to Tournament Lobby
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
